import { t } from 'i18next'
import { z } from 'zod'

import { validadorEmailDuplicado } from '@/features/proposta/brasil/domain/Comprador/validator/email-duplicado-validator'
import { EstadoCivilCompradorPrincipalValidator } from '@/features/proposta/brasil/domain/Comprador/validator/estado-civil-comprador-principal-validator'
import { EstadoCivilVinculoValidator } from '@/features/proposta/brasil/domain/Comprador/validator/estado-civil-vinculo-validator'
import {
  contatoSchema,
  estadoCivilSchema,
  transformEstadoCivil,
} from '@/features/proposta/brasil/domain/schemas/compradores/common'
import { tipoDeCliente } from '@/types/cliente'
import { tipoDoVinculoDoCompradorEnum } from '@/types/proposta'
import { StringUtils } from '@/utils'
import { cpfValidator } from '@/utils/cpf-validator'
import { nomeSobrenomeRegExp } from '@/utils/regex'

export const compradorPessoaFisicaBase = z.object({
  id: z.string().optional(),
  cpf: z
    .string()
    .min(1, { error: t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_obrigatorio') })
    .refine(
      (cpf) => cpf && cpfValidator.isValid(cpf),
      t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_invalido'),
    ),
  email: z
    .string()
    .nullish()
    .refine(
      (email) => !email || email.length === 0 || z.email().safeParse(email).success,
      t('proposta:cadastro_compradores.steps.contatos.erro.email_invalido'),
    ),
  nome: z
    .string({
      error: (issue) =>
        issue.input === undefined
          ? t('proposta:cadastro_compradores.steps.dados_comprador.erro.nome')
          : 'Tipo inválido',
    })
    .max(50, { error: t('proposta:cadastro_compradores.steps.dados_comprador.erro.nome_maximo') })
    .trim()
    .regex(nomeSobrenomeRegExp, { error: t('proposta:cadastro_compradores.steps.dados_comprador.erro.nome') })
    .transform((v) => v.trim()),
  maiorDe18Anos: z
    .enum(['sim', 'nao', ''])
    .refine((v) => v !== '', { error: t('proposta:cadastro_compradores.steps.dados_comprador.erro.maior_18') }),
  profissaoId: z.string().min(1, { error: t('proposta:cadastro_compradores.steps.dados_comprador.erro.profissao') }),
  nacionalidadeId: z
    .string()
    .min(1, { error: t('proposta:cadastro_compradores.steps.dados_comprador.erro.nacionalidade') }),
  buscouCpf: z.boolean().default(false),
})

export const compradorVinculoSchema = z
  .object({
    emailsJaVinculados: z.record(z.string(), z.string()),
    compradorVinculadoIds: z.array(z.string()).min(1, { error: 'É necessário vincular um procurador a um cliente.' }),
    tipo: z.enum([tipoDoVinculoDoCompradorEnum.Procurador, tipoDoVinculoDoCompradorEnum.Representante]),
    tipoDoCliente: z.enum(tipoDeCliente),
    imagem: z.string().nullish(),
    dados: compradorPessoaFisicaBase,
    contato: contatoSchema,
    estadoCivil: estadoCivilSchema
      .superRefine((data, ctx) => {
        const issue = new EstadoCivilVinculoValidator(data).validar()
        if (!issue) return
        ctx.addIssue({
          message: issue.message,
          path: issue.path,
          code: 'custom',
        })
      })
      .transform(transformEstadoCivil),
  })
  .extend({ mode: z.enum(['create', 'edit']) })
  .superRefine(({ dados, contato, emailsJaVinculados }, ctx) => {
    validadorEmailDuplicado(
      { documento: dados.cpf, email: contato?.email, fieldPath: ['contato', 'email'] },
      emailsJaVinculados,
      ctx,
    )
  })

const compradorMaePaiSchema = z
  .object({
    emailsJaVinculados: z.record(z.string(), z.string()),
    tipo: z.literal(tipoDoVinculoDoCompradorEnum.MaePai),
    imagem: z.string().nullish(),
    dados: compradorPessoaFisicaBase,
    contato: contatoSchema,
    estadoCivil: estadoCivilSchema
      .superRefine((data, ctx) => {
        const issue = new EstadoCivilVinculoValidator(data).validar()
        if (!issue) return
        ctx.addIssue({
          message: issue.message,
          path: issue.path,
          code: 'custom',
        })
      })
      .transform(transformEstadoCivil),
  })
  .superRefine(({ dados, contato, emailsJaVinculados }, ctx) => {
    validadorEmailDuplicado(
      { documento: dados.cpf, email: contato?.email, fieldPath: ['contato', 'email'] },
      emailsJaVinculados,
      ctx,
    )
  })

export const compradorPessoaFisicaStepSchema = z
  .object({
    emailsJaVinculados: z.record(z.string(), z.string()),
    imagem: z.string().nullish(),
    tipo: z.literal('PessoaFisica'),
    fluxoIndependente: z.enum(['MaePai', 'Conjuge']).nullish(),
    editouTipoAssinatura: z.boolean().default(false),
    tipoDoCliente: z.enum(tipoDeCliente),
    dados: compradorPessoaFisicaBase,
    contato: contatoSchema.nullish(),
    estadoCivil: estadoCivilSchema.transform(transformEstadoCivil).nullish(),
    maePai: compradorMaePaiSchema.nullish(),
    conjuge: z
      .object({
        dados: compradorPessoaFisicaBase,
        contato: contatoSchema,
        tipo: z.literal(tipoDoVinculoDoCompradorEnum.Conjuge).default(tipoDoVinculoDoCompradorEnum.Conjuge),
        imagem: z.string().nullish(),
      })
      .nullish(),
  })
  .extend({ mode: z.enum(['create', 'edit']) })
  .superRefine(({ dados, estadoCivil, mode }, ctx) => {
    if (mode === 'edit' && dados.maiorDe18Anos === 'nao' && !dados.email) {
      ctx.addIssue({
        path: ['dados', 'email'],
        code: 'custom',
        message: t('proposta:cadastro_compradores.steps.contatos.erro.email_invalido'),
      })
      return
    }

    if (dados.maiorDe18Anos === 'sim') {
      const issue = new EstadoCivilCompradorPrincipalValidator(estadoCivil).run()
      if (!issue) return
      ctx.addIssue({
        message: issue.message,
        path: issue.path,
        code: 'custom',
      })
    }
  })
  .superRefine(({ dados, contato, emailsJaVinculados }, ctx) => {
    if (dados.maiorDe18Anos === 'nao') return z.NEVER
    validadorEmailDuplicado(
      { documento: dados.cpf, email: contato?.email, fieldPath: ['contato', 'email'] },
      emailsJaVinculados,
      ctx,
    )
  })
  .superRefine(({ maePai, emailsJaVinculados }, ctx) => {
    validadorEmailDuplicado(
      { documento: maePai?.dados.cpf, email: maePai?.contato?.email, fieldPath: ['maePai', 'contato', 'email'] },
      emailsJaVinculados,
      ctx,
    )
  })
  .superRefine(({ dados, contato, conjuge, emailsJaVinculados }, ctx) => {
    if (!contato?.email) return z.NEVER
    const emailsJaVinculadosIncluindoConjuge = {
      ...emailsJaVinculados,
      [contato.email]: StringUtils.normalize(dados.cpf),
    }
    validadorEmailDuplicado(
      { documento: conjuge?.dados.cpf, email: conjuge?.contato?.email, fieldPath: ['conjuge', 'contato', 'email'] },
      emailsJaVinculadosIncluindoConjuge,
      ctx,
    )
  })

export type CompradorEstadoCivilSchemaOutput = z.output<typeof estadoCivilSchema>

export type CompradorVinculoStepSchemaInput = z.input<typeof compradorVinculoSchema>
export type CompradorVinculoStepSchemaOutput = z.output<typeof compradorVinculoSchema>

export type CompradorMaePaiStepSchemaInput = z.input<typeof compradorMaePaiSchema>

export type CompradorPessoaFisicaStepSchemaInput = z.input<typeof compradorPessoaFisicaStepSchema>
export type CompradorPessoaFisicaStepSchemaOutput = z.output<typeof compradorPessoaFisicaStepSchema>
